import { hexToRgba } from "@/utils/pieces";
import { text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { Chip, ChipProps, css, styled } from "@mui/material";
import { FC } from "react";

export type TTagVariant = "bold" | "light" | "normal";

export type TTagProps = {
  variant: TTagVariant;
  color: string;
  title: string;
  textColor?: string;
  enablePoint?: boolean;
  pointColor?: string;
} & Omit<ChipProps, "variant" | "color">;

export const Tag: FC<TTagProps> = ({ variant, color, title, textColor, enablePoint = variant === "light", pointColor = color, ...rest }) => {
  return (
    <StyledChip
      chipVariant={variant}
      backgroundColor={color}
      label={
        <StyledTypography variant={variant}>
          {enablePoint && <StyledPoint background={pointColor} />}
          <span style={{ color: textColor }}>{title}</span>
        </StyledTypography>
      }
      {...rest}
    />
  );
};

const options = { shouldForwardProp: (propName: string) => !["chipVariant", "backgroundColor"].includes(propName) };

const getTagCSS = (chipVariant: TTagVariant) => {
  switch (chipVariant) {
    case "bold":
      return BoldTagCSS;
    case "normal":
      return NormalTagCSS;
    default:
      return LightTagCSS;
  }
};

const getBackgroundColor = (chipVariant: TTagVariant, backgroundColor: string) => {
  switch (chipVariant) {
    case "bold":
    case "normal":
      return backgroundColor;
    default:
      return hexToRgba(backgroundColor, 0.2);
  }
};

const getTypographyCSS = (variant: TTagVariant) => {
  switch (variant) {
    case "normal":
      return css(typography.body14);
    default:
      return css(typography.cap12Bold);
  }
};

const StyledChip = styled(Chip, options)<{ chipVariant: TTagVariant; backgroundColor: string }>`
  ${({ chipVariant }) => getTagCSS(chipVariant)};
  background-color: ${({ chipVariant, backgroundColor }) => getBackgroundColor(chipVariant, backgroundColor)};
  padding: 4px 16px;
`;

const BoldTagCSS = css`
  border-radius: ${rounded.md};
  .MuiTypography-root {
    font-size: 12px;
    font-weight: bold;
    color: ${text.white};
  }
`;

const NormalTagCSS = css`
  border-radius: ${rounded.md};
  .MuiTypography-root {
    font-size: 14px;
    color: ${text.white};
  }
`;

const LightTagCSS = css`
  border-radius: ${rounded.lg};
  .MuiTypography-root {
    font-size: 14px;
    color: ${text.primary};
  }
`;

const StyledTypography = styled("span")<{ variant: TTagVariant }>`
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  ${({ variant }) => getTypographyCSS(variant)};
`;

const StyledPoint = styled("span")<{ background: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${rounded.full};
  background-color: ${({ background }) => background};
`;
